import { eachDayOfInterval, isAfter } from 'date-fns'
import isEqual from 'lodash/isEqual'
import { action, computed, makeObservable, observable } from 'mobx'
import { getValue } from 'mobx-async'
import { computedFn } from 'mobx-utils'

import { asyncDebounce, formatDate, parseISODate } from '@avantstay/backoffice-core'

import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'
import { CreateBookingStore } from '@new/domains/bookingsTapeChart/stores/CreateBookingStore'
import { TapeChartBlockStore } from '@new/domains/bookingsTapeChart/stores/TapeChartBlockStore'
import { CreateTapeContextProps } from '@new/domains/fastBookingsTapeChart/__types'
import {
  CustomFilterIds,
  fastBookingsTapeChartFiltersStore,
  fastBookingsTapeChartStore,
  TapeChartCreateTapeStore,
} from '@new/domains/fastBookingsTapeChart/internal'
import { TapeChartQuotesStore } from '@new/domains/fastBookingsTapeChart/stores/TapeChartQuotesStore'

import { getPreBookingPriceTotal } from '@/services/arriere/modules/bookingPrices'

export interface RangeData {
  id: string
  start: string
  end?: string
  minStay?: number
  price?: number
}

class SelectRangeStore {
  private selectedRange: RangeData | undefined
  tentativeEnd: string | undefined

  constructor() {
    makeObservable<SelectRangeStore, 'selectedRange'>(this, {
      selectedRange: observable,
      tentativeEnd: observable,
      setSelectedRange: action.bound,
      hasRange: computed,
      range: computed,
      rangeTotalPrice: computed,
      createReservation: action.bound,
      dateCellClick: action.bound,
      clearSelectedRange: action.bound,
      clearTentativeDate: action.bound,
      minStay: computed,
      maybeSetTentativeDate: action.bound,
    })
  }

  setSelectedRange(newRange?: RangeData) {
    this.selectedRange = newRange
    this.tentativeEnd = undefined
  }

  get hasRange() {
    return this.selectedRange?.start && this.selectedRange.end
  }

  get range(): RangeData | undefined {
    if (!this.selectedRange) {
      return undefined
    }
    return {
      ...this.selectedRange,
      minStay: this.minStay,
      price: this.hasRange ? getValue(this.rangeTotalPrice) : undefined,
    }
  }

  getRangeHasNightlyPrices = computedFn(
    ({ homeId, fromDate, toDate }: { homeId?: string; fromDate?: string; toDate?: string }) => {
      if (fromDate && toDate && fastBookingsTapeChartStore.selectRangeProps.datePrices) {
        const allDates = eachDayOfInterval({
          start: parseISODate(fromDate),
          end: parseISODate(toDate),
        })?.map(date => formatDate(date))

        return fastBookingsTapeChartStore.selectRangeProps.datePrices.some(
          datePrice =>
            datePrice.homeId === homeId &&
            allDates?.includes(formatDate(datePrice.date)) &&
            !!datePrice.rate,
        )
      }
      return undefined
    },
  )

  private debouncedPreBookingPriceBreakdownTotal = asyncDebounce(getPreBookingPriceTotal, 300)

  get rangeTotalPrice(): Promise<number | undefined> {
    if (!this.selectedRange || !this.selectedRange.start || !this.tentativeEnd) {
      return Promise.resolve(undefined)
    }

    return this.debouncedPreBookingPriceBreakdownTotal({
      homeId: this.selectedRange.id,
      guests: 1,
      pets: 0,
      from: this.selectedRange.start,
      until: this.tentativeEnd,
    }).then(({ total }) => total.total)
  }

  getAvailability(id: string, date: string) {
    const { blockedDatesById, blockedDatesWithSingleNight } =
      fastBookingsTapeChartStore.selectRangeProps
    const blockedDates = blockedDatesById[id]
    const isAllDaysInRangeAvailable =
      this.selectedRange?.start && blockedDates
        ? eachDayOfInterval({
            start: parseISODate(this.selectedRange.start),
            end: parseISODate(date),
          })
            .map(it => formatDate(it))
            .every(it => !blockedDates.includes(it))
        : true

    const blockedDaysWithSingleNight = blockedDatesWithSingleNight[id]
    const isAllDaysWithSingleNightAvailable =
      this.selectedRange?.start && blockedDaysWithSingleNight
        ? eachDayOfInterval({
            start: parseISODate(this.selectedRange.start),
            end: parseISODate(date),
          })
            .map(it => formatDate(it))
            .every(it => !blockedDates.includes(it))
        : true

    const hasDifferentDates = !isEqual(blockedDatesWithSingleNight, blockedDates)
    const differentDates =
      blockedDaysWithSingleNight?.filter(day => !blockedDates.includes(day)) || []
    const blockedDayIsAfterSelectedRangeStart =
      this.selectedRange?.start &&
      differentDates
        .filter(
          day =>
            day !== this.selectedRange!.start &&
            isAfter(parseISODate(day), parseISODate(this.selectedRange!.start)),
        )
        .some(day => isAfter(parseISODate(date), parseISODate(day)) || date === day)

    return {
      isAllDaysInRangeAvailable,
      isAllDaysWithSingleNightAvailable,
      hasDifferentDates,
      differentDates,
      blockedDayIsAfterSelectedRangeStart,
    }
  }

  private handleSelectDatesAction(tapeContext: CreateTapeContextProps) {
    const { selectionQuotesHomeIds } = TapeChartQuotesStore

    const { availableDates, computedRouteParams } = fastBookingsTapeChartFiltersStore
    TapeChartCreateTapeStore.setCreateTapeContextPeriod(tapeContext.period)
    if (selectionQuotesHomeIds?.length && !availableDates) {
      computedRouteParams[CustomFilterIds.AvailableDates].replace({
        checkIn: tapeContext.period?.fromDate,
        checkOut: tapeContext.period?.toDate,
        ignoreBlocks: true,
      })
    } else {
      // In case quotes tool is not active we push tapeContext to selected home
      TapeChartCreateTapeStore.setCreateTapeContext([tapeContext])

      TapeChartQuotesStore.getHomeData(tapeContext.home?.id)
      TapeChartBlockStore.toggleCreateTapeBlockModal = true
    }
  }

  createReservation() {
    if (!this.selectedRange || !this.selectedRange.end) {
      return
    }

    const { id, start, end } = this.selectedRange
    const homeInfo = fastBookingsTapeChartStore.getHomeInfoObject(id)

    BookingsTapeChartStore.setCurrentHomeId(homeInfo?.id)

    const tapeContext = {
      home: homeInfo
        ? {
            id: homeInfo.id,
            name: homeInfo.name || '',
            regionName: homeInfo.regionName || '',
          }
        : undefined,
      period: { fromDate: start, toDate: end },
    }

    const createBlockObject = {
      ...tapeContext,
      hotelRoomTypeId: homeInfo?.hotelRoomTypeId,
      guestCount: {
        adults: 1,
        children: 0,
        infants: 0,
      },
    }

    CreateBookingStore.setCreateReservationObject(createBlockObject)
    TapeChartBlockStore.setTapeBlockObject(tapeContext)

    this.handleSelectDatesAction(tapeContext)
    this.clearSelectedRange()
  }

  dateCellClick(id: string, date: string) {
    const isSelectingQuotes = TapeChartQuotesStore.canCreateQuote

    if (isSelectingQuotes && fastBookingsTapeChartFiltersStore.availableDates) {
      return
    }

    if (
      this.selectedRange !== undefined &&
      this.selectedRange.id === id &&
      isAfter(parseISODate(date), parseISODate(this.selectedRange.start))
    ) {
      // We are guaranteed to have a start date set here, so we need to figure out what to do
      // - if all dates are available between them, we're good, otherwise we're not
      const {
        blockedDayIsAfterSelectedRangeStart,
        differentDates,
        hasDifferentDates,
        isAllDaysInRangeAvailable,
        isAllDaysWithSingleNightAvailable,
      } = this.getAvailability(id, date)

      if (
        hasDifferentDates &&
        isAllDaysWithSingleNightAvailable &&
        (differentDates.includes(formatDate(date)) || blockedDayIsAfterSelectedRangeStart)
      ) {
        this.setSelectedRange({
          id,
          start: date,
        })

        return
      }

      // If they are available we just set it
      if (isAllDaysInRangeAvailable) {
        this.setSelectedRange({
          ...this.selectedRange,
          end: date,
        })

        setTimeout(this.createReservation, 500)
        return
      }
    }

    if (
      this.selectedRange &&
      this.selectedRange.id === id &&
      (this.selectedRange.start === date || this.selectedRange.end === date)
    ) {
      this.setSelectedRange(undefined)
    } else {
      this.setSelectedRange({
        id,
        start: date,
      })
    }
  }

  clearSelectedRange() {
    this.setSelectedRange(undefined)
  }

  clearTentativeDate(homeId: string) {
    if (this.selectedRange?.id === homeId) {
      this.tentativeEnd = undefined
    }
  }

  get minStay() {
    if (this.selectedRange?.start && fastBookingsTapeChartStore.selectRangeProps.datePrices) {
      const rangeStart = formatDate(this.selectedRange.start)
      const minStay = fastBookingsTapeChartStore.selectRangeProps.datePrices.find(
        datePrice =>
          formatDate(datePrice.date) === rangeStart && datePrice.homeId === this.selectedRange?.id,
      )?.minStay

      return minStay || undefined
    }
    return undefined
  }

  maybeSetTentativeDate(id: string, date: string) {
    if (
      !this.selectedRange ||
      this.selectedRange.id !== id ||
      !isAfter(parseISODate(date), parseISODate(this.selectedRange.start))
    )
      return

    const {
      blockedDayIsAfterSelectedRangeStart,
      differentDates,
      hasDifferentDates,
      isAllDaysInRangeAvailable,
      isAllDaysWithSingleNightAvailable,
    } = this.getAvailability(id, date)

    if (
      hasDifferentDates &&
      isAllDaysWithSingleNightAvailable &&
      (differentDates.includes(formatDate(date)) || blockedDayIsAfterSelectedRangeStart)
    ) {
      this.tentativeEnd = undefined
    } else if (isAllDaysInRangeAvailable) {
      this.tentativeEnd = date
    }
  }
}

export const selectRangeStore = new SelectRangeStore()
